import { TBulkMode } from '../../../services/CoursesService/interfaces'

interface IBulkFormValues {
  mode: TBulkMode
  selectedCourseIds: number[]
  discount: string
  taxaMatricula: number | null
  priceOriginal: number | null
  removeCoupon: boolean
  couponValue: string
  isScheduled?: boolean
  scheduledAt?: string
}

/**
 * Validates bulk price form fields.
 * Returns true when the form has enough data to submit.
 */
export function isValidBulkForm({
  mode,
  selectedCourseIds,
  discount,
  taxaMatricula,
  priceOriginal,
  removeCoupon,
  couponValue,
  isScheduled,
  scheduledAt
}: IBulkFormValues): boolean {
  if (selectedCourseIds.length === 0) return false
  if (isScheduled && !scheduledAt) return false
  if (mode === 'discount') return discount !== '' && parseFloat(discount) >= 0 && parseFloat(discount) <= 100
  if (mode === 'prices') return taxaMatricula != null || priceOriginal != null || discount !== ''
  if (mode === 'coupon') return removeCoupon || couponValue.trim() !== ''
  return false
}

interface IBuildPayloadParams {
  mode: TBulkMode
  selectedCourseIds: number[]
  modality: string
  discount: string
  taxaMatricula: number | null
  priceOriginal: number | null
  removeCoupon: boolean
  couponValue: string
  ctaText: string
  entries?: { course_id: number; price_type: string }[]
}

/**
 * Builds the payload object for bulk price schedule creation/update.
 */
export function buildBulkPayload({
  mode,
  selectedCourseIds,
  modality,
  discount,
  taxaMatricula,
  priceOriginal,
  removeCoupon,
  couponValue,
  ctaText,
  entries
}: IBuildPayloadParams): Record<string, unknown> {
  if (mode === 'coupon') {
    return {
      course_ids: selectedCourseIds,
      coupon: removeCoupon ? null : couponValue.trim(),
      ...(ctaText.trim() !== '' ? { cta_text: ctaText.trim() } : {})
    }
  }

  const resolvedEntries = entries ?? selectedCourseIds.map((id) => ({ course_id: id, price_type: modality }))

  return {
    entries: resolvedEntries.map((e) => ({ course_id: e.course_id, price_type: e.price_type })),
    mode,
    ...(discount !== '' ? { discount: parseFloat(discount) } : {}),
    ...(mode === 'prices' && taxaMatricula != null ? { taxa_matricula: taxaMatricula } : {}),
    ...(mode === 'prices' && priceOriginal != null ? { price_original: priceOriginal } : {}),
    ...(ctaText.trim() !== '' ? { cta_text: ctaText.trim() } : {})
  }
}
